// أجهزتي — الجلسات الحيّة، وإخراج ما ليس هذا الجهاز.
//
// الغرض الوحيد: هاتف ضاع أو جهاز استُعير. قبل هذه الشاشة كان السبيل الوحيد
// إلى ذلك تغيير كلمة المرور، وهو إجراء أثقل ويُجبر المستخدم على حفظ كلمة جديدة.
//
// لا نعرض عناوين IP: لا تعني شيئاً لمستخدم هذا التطبيق، وإظهار «جهاز مجهول من
// 213.6.x.x» يثير قلقاً بلا فائدة. الجهاز والوقت يكفيان للتعرّف.

import { esc } from '../util.js';
import { api, ApiError } from '../api.js';
import { backBar, emptyState } from '../ui.js';

export function freshDraft(){
  return { sessions: null, loading: false, err: null, busy: false };
}

/** اسم جهاز مقروء من ترويسة المتصفّح — تقريب يكفي للتعرّف لا تحليل دقيق. */
function deviceName(ua){
  if (!ua) return 'جهاز غير معروف';

  const os = /Android/i.test(ua) ? 'أندرويد'
    : /iPhone/i.test(ua) ? 'آيفون'
    : /iPad/i.test(ua) ? 'آيباد'
    : /Windows/i.test(ua) ? 'ويندوز'
    : /Mac OS X|Macintosh/i.test(ua) ? 'ماك'
    : /Linux/i.test(ua) ? 'لينكس'
    : null;

  // الترتيب مقصود: Edge و Opera يذكران Chrome في ترويستهما، و Chrome يذكر Safari
  const browser = /Edg\//i.test(ua) ? 'Edge'
    : /OPR\/|Opera/i.test(ua) ? 'Opera'
    : /Firefox\//i.test(ua) ? 'Firefox'
    : /Chrome\//i.test(ua) ? 'Chrome'
    : /Safari\//i.test(ua) ? 'Safari'
    : null;

  if (browser && os) return `${browser} على ${os}`;
  return browser || os || 'جهاز غير معروف';
}

/** «قبل ٣ دقائق» — الوقت المطلق لا يقول للمستخدم شيئاً عن جهاز يشكّ فيه. */
function ago(iso){
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 2) return 'الآن';
  if (mins < 60) return `قبل ${mins} دقيقة`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `قبل ${hours} ساعة`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'أمس' : `قبل ${days} يوماً`;
}

function sessionRow(s){
  return `<div class="switchrow">
    <span class="nm">${esc(deviceName(s.userAgent))}
      ${s.isCurrent ? '<small style="color:var(--dest)">هذا الجهاز</small>'
                    : `<small>آخر ظهور ${esc(ago(s.lastSeenAt))}</small>`}</span>
  </div>`;
}

export function render(app){
  const d = app.devicesDraft;

  if (d.loading && !d.sessions){
    return backBar('أجهزتي') + '<div class="scroll"><p class="desc">جارٍ الجلب…</p></div>';
  }
  if (d.err){
    return backBar('أجهزتي')
      + `<div class="scroll">${emptyState('تعذّر جلب الأجهزة', `<p>${esc(d.err)}</p>`)}</div>`;
  }

  const list = d.sessions || [];
  const others = list.filter(s => !s.isCurrent).length;

  return backBar('أجهزتي', `${list.length} جلسة مفتوحة`)
    + `<div class="scroll">
      <div class="card">
        <h3>الأجهزة الداخلة بحسابك</h3>
        <p class="desc">كل جهاز سجّلت دخولك منه ولم تخرج. الجلسة تنتهي وحدها بعد
          ثلاثين يوماً بلا استعمال.</p>
        ${list.map(sessionRow).join('')}
      </div>

      <div class="card">
        <h3>هاتف ضاع؟</h3>
        <p class="desc">${others
          ? `أخرج الأجهزة الأخرى كلها (${others}) وابقَ داخلاً هنا. لا يحتاج تغيير كلمة المرور.`
          : 'لا أجهزة أخرى داخلة بحسابك الآن.'}</p>
        ${others ? `<div class="btnrow">
          <button type="button" class="btn danger" data-act="dv-revoke" ${d.busy ? 'disabled' : ''}>
            أخرج الأجهزة الأخرى</button>
        </div>` : ''}
      </div>
    </div>`;
}

export async function load(app){
  const d = app.devicesDraft;
  d.loading = true;
  d.err = null;
  app.render();
  try {
    d.sessions = (await api.listSessions()).sessions;
  } catch (err){
    d.err = err instanceof ApiError && err.isOffline
      ? 'يحتاج اتصالاً' : (err.message || 'تعذّر الجلب');
  } finally {
    d.loading = false;
    app.render();
  }
}

export function mount(app){
  const d = app.devicesDraft;
  if (!d.sessions && !d.loading && !d.err) load(app);
}

export function onClick(app, btn){
  if (btn.dataset.act !== 'dv-revoke') return false;

  const d = app.devicesDraft;
  if (!confirm('ستُخرج كل الأجهزة الأخرى من حسابك. متابعة؟')) return true;

  d.busy = true;
  app.render();
  api.revokeOthers()
    .then(async () => {
      await load(app);
      app.toast('أُخرجت الأجهزة الأخرى');
    })
    .catch(err => {
      app.toast(err instanceof ApiError && err.isOffline
        ? 'يحتاج اتصالاً' : (err.message || 'تعذّر الإخراج'));
    })
    .finally(() => { d.busy = false; app.render(); });

  return true;
}
